/*
 * Buffers messages until a "BatchDone" is received
 */
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import {
    GetOrderColumnsMessage,
    GetOrderColumnsRefreshDoneMessage,
    OrderDataMessageDataItem,
    StreamMessageType,
} from 'communication/types/order/GetOrderColumnsMessage';
import { LoggingService } from '@instinet-analytics/core-lib';

/*
 * Handles batches of order messages
 */
export class OrdersMessageBatchBuffer {
    private logger: Logger;
    private batchModeCounter = 0;
    private buffer: OrderDataMessageDataItem[] = [];
    private batchIncludesClearData = false;

    // Time after which we flush this batch if we haven't received anything...
    private timeoutMs = 1000;
    private timeoutId: NodeJS.Timeout | undefined;

    public constructor(
        loggingService: LoggingService,
        private out: (
            data: OrderDataMessageDataItem[],
            refreshDone: boolean,
            clearOrders: boolean
        ) => void
    ) {
        this.logger = buildLogger('OrdersMessageBatchBuffer', loggingService);
    }

    public in(message: GetOrderColumnsMessage) {
        switch (message.type) {
            case StreamMessageType.ClearData: {
                this.startTimeoutTimer();
                this.batchIncludesClearData = true;
                this.buffer = [];
                this.logger.debug(
                    'ClearData: clearing on next flush...',
                    message
                );
                break;
            }
            case StreamMessageType.BatchData: {
                this.startTimeoutTimer();
                this.logger.debug('BatchData: batching...');
                this.buffer.push(...(message.data || []));
                break;
            }
            case StreamMessageType.BatchDone: {
                this.clearTimer();
                this.buffer.push(...(message.data || []));
                this.logger.debug(
                    `BatchDone: flushing ${this.buffer.length} messages...`
                );
                this.flush(false);
                break;
            }
            case StreamMessageType.RefreshDone: {
                this.clearTimer();
                this.handleRefreshDone(message);
            }
        }
    }

    public startNewBatch() {
        this.batchModeCounter = this.batchModeCounter + 1;
        if (this.batchModeCounter > 1) {
            this.logger.debug(
                'Batch mode counter incremented to ' + this.batchModeCounter
            );
        }
    }

    public flush(refreshDone: boolean) {
        if (
            this.buffer.length > 0 ||
            refreshDone ||
            this.batchIncludesClearData
        ) {
            this.out(this.buffer, refreshDone, this.batchIncludesClearData);
            this.buffer = [];
            this.batchIncludesClearData = false;
        }
    }

    private handleRefreshDone(message: GetOrderColumnsRefreshDoneMessage) {
        this.batchModeCounter =
            this.batchModeCounter - message.numMergedRequests;

        if (this.batchModeCounter > 0) {
            // Do not emit as the batch is not complete, but kick off the timer as we cannot trust the back end...
            this.logger.debug('RefreshDone: handled, batch not complete', {
                batchModeCounter: this.batchModeCounter,
                message,
            });
            this.startTimeoutTimer();
            return;
        }

        this.logger.debug('RefreshDone: completes batch', message);
        this.flush(true);
    }

    private startTimeoutTimer() {
        this.clearTimer();
        this.timeoutId = setTimeout(() => this.timeout(), this.timeoutMs);
    }

    private clearTimer() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.timeoutId = undefined;
    }

    private timeout() {
        // After 1 sec of receiving nothing, we flush any pending messages
        // Set refreshDone to true to make sure Portfolio Summary etc updates
        // But stay in batch mode so that we can apply RefreshDone messages to the batchModeCounter
        this.logger.warn(
            `No messages after ${this.timeoutMs}: flushing messages`
        );
        this.flush(true);
    }
}
